import React, { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { FileText, TrendingUp, Award, Users, BookOpen, Calendar, DollarSign, Download, AlertCircle, ChevronDown, ChevronRight } from "lucide-react";

const Reports = () => {
  const [academicReport, setAcademicReport] = useState(null);
  const [attendanceReport, setAttendanceReport] = useState(null);
  const [financeReport, setFinanceReport] = useState(null);
  const [outstandingReport, setOutstandingReport] = useState(null);
  const [expandedStudents, setExpandedStudents] = useState({});
  const [outstandingSearch, setOutstandingSearch] = useState("");
  const [classes, setClasses] = useState([]);
  const [students, setStudents] = useState([]);
  const [subjectsList, setSubjectsList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("academic");

  // Grade Cards state
  const now = new Date();
  const defaultAcademicStartYear = now.getMonth() >= 8 ? now.getFullYear() : now.getFullYear() - 1;
  const [gradeCardsFilters, setGradeCardsFilters] = useState({
    academic_start_month: "09", // Sept default
    academic_start_year: String(defaultAcademicStartYear),
    class_id: "all",
    student_id: "all",
    subject: "all",
    term: "all",
  });
  const [gradeCardsData, setGradeCardsData] = useState(null);
  const [gradeCardsLoading, setGradeCardsLoading] = useState(false);
  const [logoDataUrl, setLogoDataUrl] = useState(null);

  // Academic filters
  const [selectedTerm, setSelectedTerm] = useState("all");
  const [selectedClass, setSelectedClass] = useState("all");

  // Attendance filters
  const [attendanceFilters, setAttendanceFilters] = useState({
    start_date: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split("T")[0],
    end_date: new Date().toISOString().split("T")[0],
  });

  // Finance filters
  const [financeFilters, setFinanceFilters] = useState({
    start_date: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split("T")[0],
    end_date: new Date().toISOString().split("T")[0],
  });

  useEffect(() => {
    fetchClasses();
    fetchStudents();
    fetchSubjects();
    fetchAcademicReport();
    // Preload school logo as base64 data URL for PDF embedding
    (async () => {
      try {
        const res = await fetch("/taaj-logo.png");
        const blob = await res.blob();
        const reader = new FileReader();
        reader.onloadend = () => setLogoDataUrl(reader.result);
        reader.readAsDataURL(blob);
      } catch (e) {
        // Non-fatal — PDF will fall back to text-only header
        console.warn("Logo preload failed", e);
      }
    })();
  }, []);

  useEffect(() => {
    if (!loading) {
      fetchAcademicReport();
    }
  }, [selectedTerm, selectedClass]);

  const fetchClasses = async () => {
    try {
      const response = await axios.get(`${API}/classes`);
      setClasses(response.data);
    } catch (error) {
      toast.error("Failed to load classes");
    }
  };

  const fetchStudents = async () => {
    try {
      const response = await axios.get(`${API}/students`);
      setStudents(response.data);
    } catch (error) {
      console.error("Failed to load students");
    }
  };

  const fetchSubjects = async () => {
    try {
      const response = await axios.get(`${API}/subjects`);
      setSubjectsList(response.data || []);
    } catch (error) {
      console.error("Failed to load subjects");
    }
  };

  const fetchAcademicReport = async () => {
    setLoading(true);
    try {
      const params = {};
      if (selectedTerm !== "all") params.term = selectedTerm;
      if (selectedClass !== "all") params.class_id = selectedClass;

      const response = await axios.get(`${API}/reports/academic`, { params });
      setAcademicReport(response.data);
    } catch (error) {
      toast.error("Failed to load academic report");
    } finally {
      setLoading(false);
    }
  };

  const fetchAttendanceReport = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/reports/attendance`, {
        params: attendanceFilters,
      });
      setAttendanceReport(response.data);
    } catch (error) {
      toast.error("Failed to load attendance report");
    } finally {
      setLoading(false);
    }
  };

  const fetchFinanceReport = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/reports/finance`, {
        params: financeFilters,
      });
      setFinanceReport(response.data);
    } catch (error) {
      toast.error("Failed to load finance report");
    } finally {
      setLoading(false);
    }
  };

  const fetchOutstandingReport = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/reports/fees-outstanding`);
      setOutstandingReport(response.data);
    } catch (error) {
      toast.error("Failed to load fees outstanding report");
    } finally {
      setLoading(false);
    }
  };

  const toggleStudentExpanded = (studentId) => {
    setExpandedStudents((prev) => ({ ...prev, [studentId]: !prev[studentId] }));
  };

  const formatMonthLabel = (yyyyMm) => {
    if (!yyyyMm || yyyyMm === "unknown") return "Unknown";
    try {
      return new Date(yyyyMm + "-01").toLocaleDateString("en-US", {
        month: "short",
        year: "numeric",
      });
    } catch {
      return yyyyMm;
    }
  };

  // ---------- GRADE CARDS ----------
  const TERM_ORDER = ["month1", "month2", "month3", "month4", "term1", "term2"];
  const TERM_LABELS = {
    month1: "Month 1",
    month2: "Month 2",
    month3: "Month 3",
    month4: "Month 4",
    term1: "Term 1",
    term2: "Term 2",
  };

  const computeAcademicRange = () => {
    const y = parseInt(gradeCardsFilters.academic_start_year, 10);
    const m = parseInt(gradeCardsFilters.academic_start_month, 10);
    const start = new Date(y, m - 1, 1);
    const end = new Date(y + 1, m - 1, 0); // Last day of previous month, one year later
    const iso = (d) =>
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    return { start_date: iso(start), end_date: iso(end), start, end };
  };

  const getGrade = (pct) => {
    if (pct >= 90) return "A+";
    if (pct >= 80) return "A";
    if (pct >= 70) return "B";
    if (pct >= 60) return "C";
    if (pct >= 50) return "D";
    return "F";
  };

  const fetchGradeCardsData = async () => {
    setGradeCardsLoading(true);
    try {
      const { start_date, end_date } = computeAcademicRange();

      // Fetch assessments — apply class filter server-side, others client-side
      const params = {};
      if (gradeCardsFilters.class_id !== "all") params.class_id = gradeCardsFilters.class_id;
      if (gradeCardsFilters.student_id !== "all") params.student_id = gradeCardsFilters.student_id;
      const res = await axios.get(`${API}/assessments`, { params });
      let items = res.data || [];

      // Client-side filters
      items = items.filter((a) => {
        if (a.date && (a.date < start_date || a.date > end_date)) return false;
        if (gradeCardsFilters.subject !== "all" && a.subject !== gradeCardsFilters.subject) return false;
        if (gradeCardsFilters.term !== "all" && a.term !== gradeCardsFilters.term) return false;
        return true;
      });

      // Group by student
      const byStudent = {};
      items.forEach((a) => {
        if (!byStudent[a.student_id]) byStudent[a.student_id] = [];
        byStudent[a.student_id].push(a);
      });

      // Build sorted student list respecting single-student filter
      let targetStudents = students;
      if (gradeCardsFilters.student_id !== "all") {
        targetStudents = students.filter((s) => s.id === gradeCardsFilters.student_id);
      } else if (gradeCardsFilters.class_id !== "all") {
        targetStudents = students.filter((s) => s.class_id === gradeCardsFilters.class_id);
      }
      targetStudents = targetStudents.filter((s) => byStudent[s.id]);

      setGradeCardsData({
        students: targetStudents,
        assessmentsByStudent: byStudent,
        range: { start_date, end_date },
        totalAssessments: items.length,
      });
      if (targetStudents.length === 0) {
        toast.info("No assessments match the selected filters.");
      } else {
        toast.success(`Loaded ${items.length} assessment(s) for ${targetStudents.length} student(s).`);
      }
    } catch (error) {
      console.error(error);
      toast.error("Failed to load grade cards data");
    } finally {
      setGradeCardsLoading(false);
    }
  };

  const buildStudentReportRows = (studentAssessments) => {
    // rows: one per subject, columns per TERM_ORDER + Overall
    const bySubject = {};
    studentAssessments.forEach((a) => {
      const subj = (a.subject || "").trim() || "No subject";
      if (!bySubject[subj]) bySubject[subj] = {};
      if (!bySubject[subj][a.term]) bySubject[subj][a.term] = { totalMarks: 0, totalMax: 0, count: 0 };
      const cell = bySubject[subj][a.term];
      cell.totalMarks += Number(a.marks) || 0;
      cell.totalMax += Number(a.max_marks) || 0;
      cell.count += 1;
    });

    return Object.keys(bySubject)
      .sort()
      .map((subject) => {
        const row = { subject, cells: {}, overall: null };
        let totalMarks = 0;
        let totalMax = 0;
        TERM_ORDER.forEach((t) => {
          const cell = bySubject[subject][t];
          if (cell && cell.totalMax > 0) {
            const pct = (cell.totalMarks / cell.totalMax) * 100;
            row.cells[t] = {
              marks: cell.totalMarks,
              max: cell.totalMax,
              pct,
              count: cell.count,
            };
            totalMarks += cell.totalMarks;
            totalMax += cell.totalMax;
          }
        });
        if (totalMax > 0) {
          row.overall = { marks: totalMarks, max: totalMax, pct: (totalMarks / totalMax) * 100 };
        }
        return row;
      });
  };

  const exportGradeCardsPDF = () => {
    if (!gradeCardsData || gradeCardsData.students.length === 0) {
      toast.error("No data to export. Click Load first.");
      return;
    }

    const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4", compress: true });
    const pageWidth = doc.internal.pageSize.getWidth();
    const { range } = gradeCardsData;
    const yearLabel = `${new Date(range.start_date).toLocaleDateString("en-US", { month: "long", year: "numeric" })} — ${new Date(range.end_date).toLocaleDateString("en-US", { month: "long", year: "numeric" })}`;

    gradeCardsData.students.forEach((student, idx) => {
      if (idx > 0) doc.addPage();

      const studentAssessments = gradeCardsData.assessmentsByStudent[student.id] || [];
      const rows = buildStudentReportRows(studentAssessments);
      const classObj = classes.find((c) => c.id === student.class_id);
      const className = classObj?.name || "Unknown";

      // Logo (if loaded) — left side of header, deduped via alias so bulk PDFs stay small
      if (logoDataUrl) {
        try {
          doc.addImage(logoDataUrl, "PNG", 40, 20, 55, 55, "school-logo", "FAST");
        } catch (e) {
          console.warn("Logo add failed", e);
        }
      }

      // Header — school name (centered)
      doc.setFontSize(20);
      doc.setTextColor(30, 98, 74); // #1E624A
      doc.setFont("helvetica", "bold");
      doc.text("Taaj Al Waqaar Madrasa", pageWidth / 2, 40, { align: "center" });

      doc.setFontSize(13);
      doc.setTextColor(100, 100, 100);
      doc.setFont("helvetica", "normal");
      doc.text("Academic Report Card", pageWidth / 2, 60, { align: "center" });

      // Divider
      doc.setDrawColor(228, 201, 135); // #E4C987
      doc.setLineWidth(2);
      doc.line(40, 72, pageWidth - 40, 72);

      // Student info block
      doc.setFontSize(10);
      doc.setTextColor(50, 50, 50);
      const infoY = 90;
      doc.setFont("helvetica", "bold");
      doc.text("Student:", 40, infoY);
      doc.text("Class:", 40, infoY + 16);
      doc.text("Session:", 40, infoY + 32);
      doc.text("Academic Year:", pageWidth / 2 + 20, infoY);
      doc.text("Generated:", pageWidth / 2 + 20, infoY + 16);
      doc.text("Guardian:", pageWidth / 2 + 20, infoY + 32);

      doc.setFont("helvetica", "normal");
      doc.text(student.name || "-", 110, infoY);
      doc.text(className, 110, infoY + 16);
      doc.text((student.session || "-").toString(), 110, infoY + 32);
      doc.text(yearLabel, pageWidth / 2 + 110, infoY);
      doc.text(new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" }), pageWidth / 2 + 110, infoY + 16);
      doc.text(`${student.guardian_name || "-"} (${student.guardian_phone || "-"})`, pageWidth / 2 + 110, infoY + 32);

      // Build table
      const head = [["Subject", ...TERM_ORDER.map((t) => TERM_LABELS[t]), "Year Total", "Year %", "Grade"]];
      const body = rows.map((r) => {
        const cells = TERM_ORDER.map((t) => {
          const c = r.cells[t];
          if (!c) return "-";
          return `${c.marks}/${c.max}\n(${c.pct.toFixed(1)}%)`;
        });
        const yearTotal = r.overall ? `${r.overall.marks}/${r.overall.max}` : "-";
        const yearPct = r.overall ? `${r.overall.pct.toFixed(1)}%` : "-";
        const grade = r.overall ? getGrade(r.overall.pct) : "-";
        return [r.subject, ...cells, yearTotal, yearPct, grade];
      });

      // Overall summary row
      let overallMarks = 0;
      let overallMax = 0;
      rows.forEach((r) => {
        if (r.overall) {
          overallMarks += r.overall.marks;
          overallMax += r.overall.max;
        }
      });
      const overallPct = overallMax > 0 ? (overallMarks / overallMax) * 100 : 0;
      const overallGrade = overallMax > 0 ? getGrade(overallPct) : "-";

      autoTable(doc, {
        startY: 150,
        head,
        body,
        theme: "grid",
        headStyles: {
          fillColor: [30, 98, 74],
          textColor: 255,
          fontStyle: "bold",
          fontSize: 9,
          halign: "center",
        },
        bodyStyles: { fontSize: 9, halign: "center", valign: "middle" },
        columnStyles: { 0: { halign: "left", fontStyle: "bold" } },
        alternateRowStyles: { fillColor: [252, 250, 244] },
        margin: { left: 40, right: 40 },
      });

      const finalY = doc.lastAutoTable.finalY || 400;

      // Summary block
      doc.setFontSize(11);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(30, 98, 74);
      doc.text("Overall Performance", 40, finalY + 30);

      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(50, 50, 50);
      doc.text(`Total Marks: ${overallMarks} / ${overallMax}`, 40, finalY + 48);
      doc.text(`Overall Percentage: ${overallMax > 0 ? overallPct.toFixed(2) + "%" : "N/A"}`, 40, finalY + 64);
      doc.text(`Overall Grade: ${overallGrade}`, 40, finalY + 80);
      doc.text(`Total Assessments Recorded: ${studentAssessments.length}`, 40, finalY + 96);

      // Footer
      doc.setFontSize(8);
      doc.setTextColor(150, 150, 150);
      doc.text(
        `Page ${idx + 1} of ${gradeCardsData.students.length}  ·  Taaj Al Waqaar Madrasa Management System`,
        pageWidth / 2,
        doc.internal.pageSize.getHeight() - 20,
        { align: "center" }
      );
    });

    const filename =
      gradeCardsData.students.length === 1
        ? `report-card-${gradeCardsData.students[0].name.replace(/\s+/g, "-")}-${gradeCardsFilters.academic_start_year}.pdf`
        : `report-cards-${gradeCardsFilters.academic_start_year}.pdf`;
    doc.save(filename);
    toast.success(`✅ Exported ${gradeCardsData.students.length} report card(s)`);
  };
  // ---------- /GRADE CARDS ----------

  const getMedalEmoji = (index) => {
    if (index === 0) return "🥇";
    if (index === 1) return "🥈";
    if (index === 2) return "🥉";
    return "";
  };

  const getGradeColor = (average) => {
    if (average >= 90) return "bg-green-100 text-green-700";
    if (average >= 80) return "bg-blue-100 text-blue-700";
    if (average >= 70) return "bg-yellow-100 text-yellow-700";
    if (average >= 60) return "bg-orange-100 text-orange-700";
    return "bg-red-100 text-red-700";
  };

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div>
        <h2 className="text-2xl font-bold text-[#1E624A]">Reports</h2>
        <p className="text-sm text-gray-600">Warbixinnada</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="bg-white shadow-sm">
          <TabsTrigger
            value="academic"
            className="data-[state=active]:bg-[#1E624A] data-[state=active]:text-white"
          >
            📊 Academic
          </TabsTrigger>
          <TabsTrigger
            value="attendance"
            className="data-[state=active]:bg-[#1E624A] data-[state=active]:text-white"
            onClick={() => !attendanceReport && fetchAttendanceReport()}
          >
            📅 Attendance
          </TabsTrigger>
          <TabsTrigger
            value="finance"
            className="data-[state=active]:bg-[#1E624A] data-[state=active]:text-white"
            onClick={() => !financeReport && fetchFinanceReport()}
          >
            💰 Finance
          </TabsTrigger>
          <TabsTrigger
            value="outstanding"
            data-testid="outstanding-tab"
            className="data-[state=active]:bg-[#1E624A] data-[state=active]:text-white"
            onClick={() => !outstandingReport && fetchOutstandingReport()}
          >
            🧾 Fees Outstanding
          </TabsTrigger>
          <TabsTrigger
            value="grade-cards"
            data-testid="grade-cards-tab"
            className="data-[state=active]:bg-[#1E624A] data-[state=active]:text-white"
          >
            🎓 Grade Cards
          </TabsTrigger>
        </TabsList>

        {/* ACADEMIC TAB */}
        <TabsContent value="academic" className="space-y-6 mt-6">
          {/* Filters */}
          <div className="bg-white rounded-xl p-4 shadow-md">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label className="text-sm text-gray-700 mb-2">Term</Label>
                <Select value={selectedTerm} onValueChange={setSelectedTerm}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Terms</SelectItem>
                    <SelectItem value="term1">Term 1</SelectItem>
                    <SelectItem value="term2">Term 2</SelectItem>
                    <SelectItem value="term3">Term 3</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm text-gray-700 mb-2">Class</Label>
                <Select value={selectedClass} onValueChange={setSelectedClass}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Classes</SelectItem>
                    {classes.map((cls) => (
                      <SelectItem key={cls.id} value={cls.id}>
                        {cls.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-end">
                <Button
                  onClick={fetchAcademicReport}
                  className="w-full bg-[#1E624A] hover:bg-[#164935]"
                >
                  Generate Report
                </Button>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="spinner"></div>
            </div>
          ) : academicReport ? (
            <>
              {/* Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-white rounded-xl p-6 shadow-md">
                  <div className="flex items-center gap-3">
                    <Users className="text-[#1E624A]" size={32} />
                    <div>
                      <p className="text-sm text-gray-600">Total Students</p>
                      <p className="text-2xl font-bold text-gray-900">{academicReport.total_students}</p>
                    </div>
                  </div>
                </div>
                <div className="bg-white rounded-xl p-6 shadow-md">
                  <div className="flex items-center gap-3">
                    <BookOpen className="text-blue-500" size={32} />
                    <div>
                      <p className="text-sm text-gray-600">Total Classes</p>
                      <p className="text-2xl font-bold text-gray-900">{academicReport.total_classes}</p>
                    </div>
                  </div>
                </div>
                <div className="bg-white rounded-xl p-6 shadow-md">
                  <div className="flex items-center gap-3">
                    <FileText className="text-orange-500" size={32} />
                    <div>
                      <p className="text-sm text-gray-600">Total Assessments</p>
                      <p className="text-2xl font-bold text-gray-900">{academicReport.total_assessments}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Class Performance Chart */}
              <div className="bg-white rounded-xl p-6 shadow-md">
                <h3 className="text-lg font-bold text-[#1E624A] mb-4 flex items-center gap-2">
                  <TrendingUp size={20} />
                  Average Performance by Class
                </h3>
                <div className="space-y-3">
                  {Object.entries(academicReport.class_performance || {}).map(([className, average]) => (
                    <div key={className}>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="font-medium text-gray-700">{className}</span>
                        <span className="font-bold text-[#1E624A]">{average}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-3">
                        <div
                          className="bg-[#1E624A] h-3 rounded-full transition-all"
                          style={{ width: `${average}%` }}
                        ></div>
                      </div>
                    </div>
                  ))}
                  {Object.keys(academicReport.class_performance || {}).length === 0 && (
                    <p className="text-center text-gray-500 py-4">No class performance data available</p>
                  )}
                </div>
              </div>

              {/* Top & Bottom Performers */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-white rounded-xl p-6 shadow-md">
                  <h3 className="text-lg font-bold text-green-600 mb-4 flex items-center gap-2">
                    <Award size={20} />
                    🏆 Top 5 Students
                  </h3>
                  <div className="space-y-3">
                    {academicReport.top_performers && academicReport.top_performers.length > 0 ? (
                      academicReport.top_performers.map((student, index) => (
                        <div key={index} className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                          <div className="flex items-center gap-3">
                            <span className="text-2xl">{getMedalEmoji(index)}</span>
                            <div>
                              <p className="font-semibold text-gray-900">{student.name}</p>
                              <p className="text-xs text-gray-600">{student.total_assessments} assessments</p>
                            </div>
                          </div>
                          <span className={`px-3 py-1 rounded-full text-sm font-bold ${getGradeColor(student.average)}`}>
                            {student.average}%
                          </span>
                        </div>
                      ))
                    ) : (
                      <p className="text-center text-gray-500 py-4">No data available</p>
                    )}
                  </div>
                </div>

                <div className="bg-white rounded-xl p-6 shadow-md">
                  <h3 className="text-lg font-bold text-orange-600 mb-4 flex items-center gap-2">
                    ⚠️ Bottom 5 Students
                  </h3>
                  <div className="space-y-3">
                    {academicReport.bottom_performers && academicReport.bottom_performers.length > 0 ? (
                      academicReport.bottom_performers.map((student, index) => (
                        <div key={index} className="flex items-center justify-between p-3 bg-orange-50 rounded-lg">
                          <div>
                            <p className="font-semibold text-gray-900">{student.name}</p>
                            <p className="text-xs text-gray-600">{student.total_assessments} assessments</p>
                          </div>
                          <span className={`px-3 py-1 rounded-full text-sm font-bold ${getGradeColor(student.average)}`}>
                            {student.average}%
                          </span>
                        </div>
                      ))
                    ) : (
                      <p className="text-center text-gray-500 py-4">No data available</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Subject Averages */}
              <div className="bg-white rounded-xl p-6 shadow-md">
                <h3 className="text-lg font-bold text-[#1E624A] mb-4 flex items-center gap-2">
                  📘 Subject-wise Averages
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {Object.entries(academicReport.subject_averages || {}).map(([subject, average]) => (
                    <div key={subject} className="p-4 bg-gradient-to-br from-[#E4C987]/20 to-[#1E624A]/10 rounded-lg">
                      <p className="text-sm font-medium text-gray-700 mb-1">{subject}</p>
                      <div className="flex items-baseline gap-2">
                        <p className="text-3xl font-bold text-[#1E624A]">{average}%</p>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                        <div
                          className="bg-[#E4C987] h-2 rounded-full transition-all"
                          style={{ width: `${average}%` }}
                        ></div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          ) : null}
        </TabsContent>

        {/* ATTENDANCE TAB */}
        <TabsContent value="attendance" className="space-y-6 mt-6">
          <div className="bg-white rounded-xl p-4 shadow-md">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label>Start Date</Label>
                <Input
                  type="date"
                  value={attendanceFilters.start_date}
                  onChange={(e) =>
                    setAttendanceFilters({ ...attendanceFilters, start_date: e.target.value })
                  }
                />
              </div>
              <div>
                <Label>End Date</Label>
                <Input
                  type="date"
                  value={attendanceFilters.end_date}
                  onChange={(e) =>
                    setAttendanceFilters({ ...attendanceFilters, end_date: e.target.value })
                  }
                />
              </div>
              <div className="flex items-end">
                <Button onClick={fetchAttendanceReport} className="w-full bg-[#1E624A] hover:bg-[#164935]">
                  Generate Report
                </Button>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="spinner"></div>
            </div>
          ) : attendanceReport ? (
            <>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-white rounded-xl p-6 shadow-md">
                  <div className="flex items-center gap-3">
                    <Calendar className="text-[#1E624A]" size={32} />
                    <div>
                      <p className="text-sm text-gray-600">Total Records</p>
                      <p className="text-2xl font-bold text-gray-900">{attendanceReport.total_records || 0}</p>
                    </div>
                  </div>
                </div>
                <div className="bg-white rounded-xl p-6 shadow-md">
                  <div className="flex items-center gap-3">
                    <Users className="text-green-500" size={32} />
                    <div>
                      <p className="text-sm text-gray-600">Present</p>
                      <p className="text-2xl font-bold text-gray-900">{attendanceReport.total_present || 0}</p>
                    </div>
                  </div>
                </div>
                <div className="bg-white rounded-xl p-6 shadow-md">
                  <div className="flex items-center gap-3">
                    <TrendingUp className="text-blue-500" size={32} />
                    <div>
                      <p className="text-sm text-gray-600">Attendance %</p>
                      <p className="text-2xl font-bold text-gray-900">{attendanceReport.attendance_percentage || 0}%</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-md">
                <h3 className="text-lg font-bold text-[#1E624A] mb-4">Attendance Summary</h3>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Total Present:</span>
                    <span className="font-semibold text-green-600">{attendanceReport.total_present || 0}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Total Absent:</span>
                    <span className="font-semibold text-red-600">{attendanceReport.total_absent || 0}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Total Late:</span>
                    <span className="font-semibold text-orange-600">{attendanceReport.total_late || 0}</span>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="bg-white rounded-xl p-12 text-center text-gray-500">
              Click &quot;Generate Report&quot; to view attendance analytics
            </div>
          )}
        </TabsContent>

        {/* FINANCE TAB */}
        <TabsContent value="finance" className="space-y-6 mt-6">
          <div className="bg-white rounded-xl p-4 shadow-md">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label>Start Date</Label>
                <Input
                  type="date"
                  value={financeFilters.start_date}
                  onChange={(e) =>
                    setFinanceFilters({ ...financeFilters, start_date: e.target.value })
                  }
                />
              </div>
              <div>
                <Label>End Date</Label>
                <Input
                  type="date"
                  value={financeFilters.end_date}
                  onChange={(e) =>
                    setFinanceFilters({ ...financeFilters, end_date: e.target.value })
                  }
                />
              </div>
              <div className="flex items-end">
                <Button onClick={fetchFinanceReport} className="w-full bg-[#1E624A] hover:bg-[#164935]">
                  Generate Report
                </Button>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="spinner"></div>
            </div>
          ) : financeReport ? (
            <>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <div className="bg-white rounded-xl p-6 shadow-md">
                  <div className="flex items-center gap-3">
                    <DollarSign className="text-[#1E624A]" size={32} />
                    <div>
                      <p className="text-sm text-gray-600">Total Billed</p>
                      <p className="text-2xl font-bold text-gray-900">${financeReport.total_billed?.toFixed(2) || 0}</p>
                    </div>
                  </div>
                </div>
                <div className="bg-white rounded-xl p-6 shadow-md">
                  <div className="flex items-center gap-3">
                    <DollarSign className="text-green-500" size={32} />
                    <div>
                      <p className="text-sm text-gray-600">Collected</p>
                      <p className="text-2xl font-bold text-gray-900">${financeReport.total_collected?.toFixed(2) || 0}</p>
                    </div>
                  </div>
                </div>
                <div className="bg-white rounded-xl p-6 shadow-md">
                  <div className="flex items-center gap-3">
                    <DollarSign className="text-red-500" size={32} />
                    <div>
                      <p className="text-sm text-gray-600">Expenses</p>
                      <p className="text-2xl font-bold text-gray-900">${financeReport.total_expenses?.toFixed(2) || 0}</p>
                    </div>
                  </div>
                </div>
                <div className="bg-white rounded-xl p-6 shadow-md">
                  <div className="flex items-center gap-3">
                    <TrendingUp className="text-blue-500" size={32} />
                    <div>
                      <p className="text-sm text-gray-600">Net Income</p>
                      <p className="text-2xl font-bold text-gray-900">${financeReport.net_income?.toFixed(2) || 0}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-md">
                <h3 className="text-lg font-bold text-[#1E624A] mb-4">Collection Rate</h3>
                <div className="space-y-3">
                  <div className="flex justify-between text-sm mb-1">
                    <span className="font-medium text-gray-700">Collection Percentage</span>
                    <span className="font-bold text-[#1E624A]">{financeReport.collection_percentage || 0}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-4">
                    <div
                      className="bg-[#1E624A] h-4 rounded-full transition-all"
                      style={{ width: `${financeReport.collection_percentage || 0}%` }}
                    ></div>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="bg-white rounded-xl p-12 text-center text-gray-500">
              Click &quot;Generate Report&quot; to view finance analytics
            </div>
          )}
        </TabsContent>

        {/* FEES OUTSTANDING TAB */}
        <TabsContent value="outstanding" className="space-y-6 mt-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <p className="text-sm text-gray-600">
                Every active student with unpaid invoices — including previous months&apos; unpaid balances carried over until cleared.
              </p>
            </div>
            <Button
              onClick={fetchOutstandingReport}
              data-testid="refresh-outstanding-btn"
              className="bg-[#1E624A] hover:bg-[#164935]"
            >
              Refresh
            </Button>
          </div>

          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="spinner"></div>
            </div>
          ) : outstandingReport ? (
            <>
              {/* Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-white rounded-xl p-6 shadow-md border-l-4 border-red-500">
                  <div className="flex items-center gap-3">
                    <AlertCircle className="text-red-500" size={32} />
                    <div>
                      <p className="text-sm text-gray-600">Total Outstanding</p>
                      <p className="text-2xl font-bold text-red-600" data-testid="total-outstanding-value">
                        ${outstandingReport.total_outstanding?.toFixed(2) || "0.00"}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="bg-white rounded-xl p-6 shadow-md border-l-4 border-[#E4C987]">
                  <div className="flex items-center gap-3">
                    <Users className="text-[#E4C987]" size={32} />
                    <div>
                      <p className="text-sm text-gray-600">Students With Dues</p>
                      <p className="text-2xl font-bold text-gray-900">
                        {outstandingReport.students_with_dues_count} / {outstandingReport.total_active_students}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="bg-white rounded-xl p-6 shadow-md border-l-4 border-[#1E624A]">
                  <div className="flex items-center gap-3">
                    <DollarSign className="text-[#1E624A]" size={32} />
                    <div>
                      <p className="text-sm text-gray-600">Avg. Debt per Student</p>
                      <p className="text-2xl font-bold text-gray-900">
                        ${outstandingReport.students_with_dues_count > 0
                          ? (outstandingReport.total_outstanding / outstandingReport.students_with_dues_count).toFixed(2)
                          : "0.00"}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Search */}
              <div className="bg-white rounded-xl p-4 shadow-md">
                <Label htmlFor="outstanding-search" className="text-sm text-gray-700 mb-2">
                  Search by student, class, or guardian
                </Label>
                <Input
                  id="outstanding-search"
                  data-testid="outstanding-search-input"
                  placeholder="Type to filter..."
                  value={outstandingSearch}
                  onChange={(e) => setOutstandingSearch(e.target.value)}
                />
              </div>

              {/* Students Table */}
              <div className="bg-white rounded-xl shadow-md overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider w-8"></th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Student</th>
                        <th className="hidden md:table-cell px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Class</th>
                        <th className="hidden lg:table-cell px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Guardian</th>
                        <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider">Oldest Unpaid</th>
                        <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider">Unpaid Months</th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider">Total Owed</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {outstandingReport.students
                        .filter((s) => {
                          const q = outstandingSearch.trim().toLowerCase();
                          if (!q) return true;
                          return (
                            s.student_name?.toLowerCase().includes(q) ||
                            s.class_name?.toLowerCase().includes(q) ||
                            s.guardian_name?.toLowerCase().includes(q) ||
                            s.guardian_phone?.toLowerCase().includes(q)
                          );
                        })
                        .map((s) => {
                          const isExpanded = !!expandedStudents[s.student_id];
                          return (
                            <React.Fragment key={s.student_id}>
                              <tr
                                data-testid={`outstanding-row-${s.student_id}`}
                                className="hover:bg-gray-50 transition-colors cursor-pointer"
                                onClick={() => toggleStudentExpanded(s.student_id)}
                              >
                                <td className="px-4 py-3 align-middle">
                                  {isExpanded ? (
                                    <ChevronDown size={16} className="text-gray-500" />
                                  ) : (
                                    <ChevronRight size={16} className="text-gray-500" />
                                  )}
                                </td>
                                <td className="px-4 py-3 align-middle">
                                  <div className="font-medium text-gray-900">{s.student_name}</div>
                                  {s.fee_type === "discounted" && s.discount_percentage > 0 && (
                                    <div className="text-xs text-yellow-700">
                                      {s.discount_percentage}% discount applied
                                    </div>
                                  )}
                                </td>
                                <td className="hidden md:table-cell px-4 py-3 align-middle text-sm text-gray-700">
                                  {s.class_name}
                                  {s.session && (
                                    <span className="ml-2 text-xs text-gray-500 capitalize">({s.session})</span>
                                  )}
                                </td>
                                <td className="hidden lg:table-cell px-4 py-3 align-middle text-sm text-gray-700">
                                  <div>{s.guardian_name || "-"}</div>
                                  <div className="text-xs text-gray-500">{s.guardian_phone || ""}</div>
                                </td>
                                <td className="px-4 py-3 text-center align-middle text-sm text-gray-700">
                                  {formatMonthLabel(s.oldest_unpaid_month)}
                                </td>
                                <td className="px-4 py-3 text-center align-middle">
                                  <span className="inline-flex items-center justify-center px-2.5 py-0.5 rounded-full bg-red-100 text-red-700 text-xs font-semibold">
                                    {s.unpaid_months_count}
                                  </span>
                                </td>
                                <td className="px-4 py-3 text-right align-middle font-semibold text-red-600">
                                  ${s.total_outstanding.toFixed(2)}
                                </td>
                              </tr>
                              {isExpanded && (
                                <tr className="bg-gray-50">
                                  <td colSpan={7} className="px-4 py-4">
                                    <div className="text-xs uppercase tracking-wider text-gray-500 mb-2">
                                      Month-by-month breakdown (oldest first)
                                    </div>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                                      {s.unpaid_months.map((m) => (
                                        <div
                                          key={m.month}
                                          className="bg-white border border-gray-200 rounded-lg p-3 flex items-center justify-between"
                                        >
                                          <div>
                                            <div className="font-medium text-gray-900 text-sm">
                                              {formatMonthLabel(m.month)}
                                            </div>
                                            <div className="text-xs text-gray-500">
                                              {m.invoice_count} invoice{m.invoice_count > 1 ? "s" : ""}
                                              {m.types.length > 0 && ` · ${m.types.join(", ")}`}
                                            </div>
                                          </div>
                                          <div className="text-red-600 font-semibold text-sm">
                                            ${m.amount.toFixed(2)}
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          );
                        })}
                      {outstandingReport.students.length === 0 && (
                        <tr>
                          <td colSpan={7} className="px-4 py-12 text-center text-gray-500">
                            🎉 No outstanding fees — every active student is fully paid up.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          ) : (
            <div className="bg-white rounded-xl p-12 text-center text-gray-500">
              Loading fees outstanding report...
            </div>
          )}
        </TabsContent>

        {/* GRADE CARDS TAB */}
        <TabsContent value="grade-cards" className="space-y-6 mt-6">
          <div className="bg-white rounded-xl p-4 shadow-md space-y-4">
            <div>
              <h3 className="text-base font-semibold text-gray-900">Report Card Filters</h3>
              <p className="text-xs text-gray-500">Configure the academic year and filters, then load data and export as PDF.</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <Label className="text-sm text-gray-700 mb-2">Academic Year Start Month</Label>
                <Select
                  value={gradeCardsFilters.academic_start_month}
                  onValueChange={(v) => setGradeCardsFilters((p) => ({ ...p, academic_start_month: v }))}
                >
                  <SelectTrigger data-testid="grade-cards-start-month">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[
                      ["01", "January"], ["02", "February"], ["03", "March"], ["04", "April"],
                      ["05", "May"], ["06", "June"], ["07", "July"], ["08", "August"],
                      ["09", "September"], ["10", "October"], ["11", "November"], ["12", "December"],
                    ].map(([v, l]) => (
                      <SelectItem key={v} value={v}>{l}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm text-gray-700 mb-2">Academic Year Start</Label>
                <Input
                  type="number"
                  data-testid="grade-cards-start-year"
                  value={gradeCardsFilters.academic_start_year}
                  onChange={(e) => setGradeCardsFilters((p) => ({ ...p, academic_start_year: e.target.value }))}
                  min="2020"
                  max="2100"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Covers {new Date(`${gradeCardsFilters.academic_start_year}-${gradeCardsFilters.academic_start_month}-01`).toLocaleDateString("en-US", { month: "short", year: "numeric" })} – {new Date(`${parseInt(gradeCardsFilters.academic_start_year, 10) + 1}-${gradeCardsFilters.academic_start_month}-01`).toLocaleDateString("en-US", { month: "short", year: "numeric" })}
                </p>
              </div>
              <div>
                <Label className="text-sm text-gray-700 mb-2">Class</Label>
                <Select
                  value={gradeCardsFilters.class_id}
                  onValueChange={(v) => setGradeCardsFilters((p) => ({ ...p, class_id: v, student_id: "all" }))}
                >
                  <SelectTrigger data-testid="grade-cards-class-filter">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Classes</SelectItem>
                    {classes.map((cls) => (
                      <SelectItem key={cls.id} value={cls.id}>{cls.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm text-gray-700 mb-2">Student</Label>
                <Select
                  value={gradeCardsFilters.student_id}
                  onValueChange={(v) => setGradeCardsFilters((p) => ({ ...p, student_id: v }))}
                >
                  <SelectTrigger data-testid="grade-cards-student-filter">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Students</SelectItem>
                    {students
                      .filter((s) => gradeCardsFilters.class_id === "all" || s.class_id === gradeCardsFilters.class_id)
                      .map((s) => (
                        <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm text-gray-700 mb-2">Subject</Label>
                <Select
                  value={gradeCardsFilters.subject}
                  onValueChange={(v) => setGradeCardsFilters((p) => ({ ...p, subject: v }))}
                >
                  <SelectTrigger data-testid="grade-cards-subject-filter">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Subjects</SelectItem>
                    {subjectsList.map((s) => (
                      <SelectItem key={s.id} value={s.name}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm text-gray-700 mb-2">Assessment Period</Label>
                <Select
                  value={gradeCardsFilters.term}
                  onValueChange={(v) => setGradeCardsFilters((p) => ({ ...p, term: v }))}
                >
                  <SelectTrigger data-testid="grade-cards-term-filter">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Periods</SelectItem>
                    <SelectItem value="month1">Month 1</SelectItem>
                    <SelectItem value="month2">Month 2</SelectItem>
                    <SelectItem value="month3">Month 3</SelectItem>
                    <SelectItem value="month4">Month 4</SelectItem>
                    <SelectItem value="term1">Term 1</SelectItem>
                    <SelectItem value="term2">Term 2</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-2 pt-2">
              <Button
                onClick={fetchGradeCardsData}
                data-testid="load-grade-cards-btn"
                disabled={gradeCardsLoading}
                variant="outline"
                className="border-[#1E624A] text-[#1E624A] hover:bg-[#1E624A]/5"
              >
                {gradeCardsLoading ? "Loading..." : "Load / Preview"}
              </Button>
              <Button
                onClick={exportGradeCardsPDF}
                data-testid="export-grade-cards-pdf-btn"
                disabled={!gradeCardsData || gradeCardsData.students.length === 0}
                className="bg-[#1E624A] hover:bg-[#164935]"
              >
                <Download size={16} className="mr-2" />
                Export PDF ({gradeCardsData?.students.length || 0} student{(gradeCardsData?.students.length || 0) === 1 ? "" : "s"})
              </Button>
            </div>
          </div>

          {/* Preview */}
          {gradeCardsData && gradeCardsData.students.length > 0 && (
            <div className="bg-white rounded-xl p-6 shadow-md">
              <h3 className="text-lg font-bold text-[#1E624A] mb-4 flex items-center gap-2">
                <FileText size={20} />
                Preview — {gradeCardsData.students.length} student{gradeCardsData.students.length === 1 ? "" : "s"} · {gradeCardsData.totalAssessments} assessments
              </h3>
              <div className="space-y-6">
                {gradeCardsData.students.slice(0, 3).map((student) => {
                  const rows = buildStudentReportRows(gradeCardsData.assessmentsByStudent[student.id] || []);
                  const classObj = classes.find((c) => c.id === student.class_id);
                  return (
                    <div key={student.id} className="border border-gray-200 rounded-lg p-4">
                      <div className="mb-3">
                        <p className="font-semibold text-gray-900">{student.name}</p>
                        <p className="text-xs text-gray-500">{classObj?.name || "Unknown"} · {student.session}</p>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-2 py-2 text-left text-xs font-semibold text-gray-700">Subject</th>
                              {TERM_ORDER.map((t) => (
                                <th key={t} className="px-2 py-2 text-center text-xs font-semibold text-gray-700">{TERM_LABELS[t]}</th>
                              ))}
                              <th className="px-2 py-2 text-center text-xs font-semibold text-[#1E624A]">Year %</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100">
                            {rows.map((r) => (
                              <tr key={r.subject}>
                                <td className="px-2 py-2 font-medium">{r.subject}</td>
                                {TERM_ORDER.map((t) => {
                                  const c = r.cells[t];
                                  return (
                                    <td key={t} className="px-2 py-2 text-center text-gray-700">
                                      {c ? `${c.marks}/${c.max} (${c.pct.toFixed(0)}%)` : "-"}
                                    </td>
                                  );
                                })}
                                <td className="px-2 py-2 text-center font-semibold text-[#1E624A]">
                                  {r.overall ? `${r.overall.pct.toFixed(1)}%` : "-"}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  );
                })}
                {gradeCardsData.students.length > 3 && (
                  <p className="text-sm text-gray-500 text-center">
                    ... and {gradeCardsData.students.length - 3} more student{gradeCardsData.students.length - 3 === 1 ? "" : "s"} in the exported PDF.
                  </p>
                )}
              </div>
            </div>
          )}

          {gradeCardsData && gradeCardsData.students.length === 0 && (
            <div className="bg-white rounded-xl p-12 text-center text-gray-500">
              No assessments found for the selected filters. Try widening the date range or clearing filters.
            </div>
          )}

          {!gradeCardsData && !gradeCardsLoading && (
            <div className="bg-white rounded-xl p-12 text-center text-gray-500">
              Configure filters above and click &quot;Load / Preview&quot; to see the data before exporting.
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Reports;
